<template>
	<journey-wrapper save @initialised="init">
		<div v-if="store.showActions">
			<app-panel v-if="store.surveySaved" title="Customer Survey" compact>
				<dl class="divide-y divide-gray-100">
					<list-group-item
						v-for="(value, index) in store.filteredSurvey"
						:key="index"
						:label="value.question"
						:value="value.answer" />
				</dl>
			</app-panel>

			<!-- INITIAL -->
			<app-panel v-else-if="skipping === null" title="Customer Survey">
				<p class="text-sm">Is the customer willing to take part in the feedback survey?</p>

				<div class="flex justify-stretch gap-3 mt-4">
					<app-button class="w-full" size="large" theme="outline" @click="skipSurvey()"
						>No</app-button
					>
					<app-button class="w-full" size="large" theme="outline" @click="startSurvey()"
						>Yes</app-button
					>
				</div>
			</app-panel>

			<!-- SKIPPED -->
			<app-panel v-else-if="skipping === true" title="Skip Survey">
				<fieldset aria-label="Reason for skipping survey">
					<p class="text-gray-600 text-sm mb-4">
						Please select a reason for skipping the survey:
					</p>

					<app-radio-group v-model="store.survey![0].answer" :options="skipOptions" />

					<app-input
						v-if="store.survey![0].answer == 'Other'"
						v-model="store.journey!.survey_skip_reason"
						type="textarea"
						:rows="3"
						class="mt-3"
						placeholder="Please add details" />
				</fieldset>
			</app-panel>

			<!-- SURVEY -->
			<app-panel v-else-if="skipping === false" title="Customer Survey">
				<fieldset class="mb-4" :aria-label="store.surveyQuestions[1]">
					<legend class="text-sm">{{ store.surveyQuestions[1] }}</legend>

					<app-radio-group
						v-model="store.survey![1].answer"
						:options="yesOrNo"
						class="mt-3" />
				</fieldset>

				<fieldset class="mb-4" :aria-label="store.surveyQuestions[2]">
					<legend class="text-sm">
						{{ store.surveyQuestions[2] }}
					</legend>

					<app-radio-group
						v-model="store.survey![2].answer"
						:options="confidence"
						class="mt-3" />
				</fieldset>

				<fieldset class="mb-4" :aria-label="store.surveyQuestions[3]">
					<legend class="text-sm">
						{{ store.surveyQuestions[3] }}
					</legend>

					<app-radio-group
						v-model="store.survey![3].answer"
						:options="likeliness"
						class="mt-3" />
				</fieldset>

				<div class="mb-4">
					<label for="comments" class="text-sm mb-2 block">
						{{ store.surveyQuestions[4] }}
					</label>

					<app-input
						id="comments"
						v-model="store.survey![4].answer"
						name="comments"
						placeholder="Enter comments here"
						type="textarea"
						:rows="3" />
				</div>

				<div>
					<label for="customer_name" class="text-sm mb-2 block">
						{{ store.surveyQuestions[5] }}
					</label>
					<app-input
						id="customer_name"
						v-model="store.survey![5].answer"
						name="customer_name"
						placeholder="Enter customer name" />
				</div>
			</app-panel>
		</div>

		<journey-nav
			:current="4"
			prev="signature"
			next="confirm"
			:disabled="!store.step4Complete"
			:method="!store.surveySaved ? store.saveSurvey : undefined">
			<div
				v-if="skipping && !store.surveySaved"
				class="flex underline text-gray-500 text-sm justify-center">
				<button @click="startSurvey()">Take the Survey</button>
			</div>
		</journey-nav>
	</journey-wrapper>
</template>

<script lang="ts" setup>
	import { ref } from 'vue'
	import JourneyNav from '@/components/partials/driver-app/JourneyNav.vue'
	import useDriverApp from '@/stores/driver-app'
	import JourneyWrapper from '@/components/layout/driver-app/JourneyWrapper.vue'
	import ListGroupItem from '@/components/partials/ListGroupItem.vue'

	// Resources
	const store = useDriverApp()

	// Data
	const skipping = ref<boolean | null>(null)

	const skipOptions = [
		{ value: 1, name: 'Survey not working' },
		{ value: 2, name: 'Customer requested no survey' },
		{ value: 3, name: 'Other' }
	]

	const yesOrNo = [
		{ value: 'yes', name: 'Yes' },
		{ value: 'no', name: 'No' }
	]

	const likeliness = [
		{ value: 0, name: 'Extremely likely' },
		{ value: 1, name: 'Likely' },
		{ value: 2, name: 'Neutral' },
		{ value: 3, name: 'Unlikely' },
		{ value: 4, name: 'Very unlikely' },
		{ value: 5, name: 'Extremely unlikely' }
	]

	const confidence = [
		{ value: 0, name: 'Highly confident' },
		{ value: 1, name: 'Very confident' },
		{ value: 2, name: 'Moderately confident' },
		{ value: 3, name: 'Somewhat confident' },
		{ value: 4, name: 'Slightly confident' },
		{ value: 5, name: 'Not at all confident' }
	]

	function skipSurvey() {
		store.resetSurvey()
		skipping.value = true
	}

	function startSurvey() {
		store.resetSurvey()
		skipping.value = false
	}

	function init() {
		if (store.survey?.[0].answer) {
			skipping.value = true
		} else if (store.step4Complete) {
			skipping.value = false
		}
	}
</script>
